import { z } from "zod";

export class ApiResponseError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiResponseError";
    this.status = status;
    this.body = body;
  }
}

const ensureJson = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new ApiResponseError("Expected JSON response", response.status);
  }
  return response.json().catch(() => {
    throw new ApiResponseError("Failed to parse JSON response", response.status);
  });
};

const extractErrorMessage = async (response: Response): Promise<{ message: string; body?: unknown }> => {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const payload = await response.json().catch(() => null);
    if (payload && typeof payload === "object") {
      const message =
        typeof (payload as { message?: unknown }).message === "string"
          ? ((payload as { message?: unknown }).message as string)
          : `Request failed (${response.status})`;
      return { message, body: payload ?? undefined };
    }
  }
  const text = await response.text().catch(() => "").then((value) => value.trim());
  return {
    message: text.length > 0 ? text : `Request failed (${response.status})`,
  };
};

export const assertOk = async (response: Response): Promise<void> => {
  if (response.ok) {
    return;
  }
  const { message, body } = await extractErrorMessage(response);
  throw new ApiResponseError(message, response.status, body);
};

export const parseJson = async <T>(response: Response, schema: z.ZodType<T>): Promise<T> => {
  const payload = await ensureJson(response);
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiResponseError("Response validation failed", response.status, payload ?? undefined);
  }
  return parsed.data;
};
